<script setup lang="ts">
/**
 * Access requests — people asking to use an assistant they can see in the
 * Hub but cannot open.
 *
 * Handling is manual by design. The buttons here only record what was
 * decided; access itself is given the usual way (add the person to a team,
 * or as a named member, under Assistants), so there is still exactly one
 * path to an entitlement. "Has access now" is re-evaluated on every load,
 * so a request that was already resolved some other way is plain to see.
 *
 * The 90-day summary on top answers the evaluation question — which
 * assistants people keep asking for, and what happened to those requests.
 *
 * Took over the slot of the retired "Beta signups" tab; that table is kept
 * in the database but no longer shown.
 */
import { computed, onMounted, ref } from "vue";

import {
  AccessRequestError,
  getAccessRequestSummary,
  handleAccessRequest,
  listAccessRequests,
  type AccessRequestSummary,
  type AdminAccessRequestRow,
} from "../api/accessRequests";
import { useToast } from "../composables/useToast";
import SkeletonRows from "./SkeletonRows.vue";

const props = defineProps<{ devUser: string }>();
const toast = useToast();

const which = ref<"open" | "all">("open");
const rows = ref<AdminAccessRequestRow[]>([]);
const summary = ref<AccessRequestSummary | null>(null);
const loading = ref(false);
const busyId = ref<number | null>(null);
/** Per-row draft of the handler note, keyed by request id. */
const notes = ref<Record<number, string>>({});

const topAssistants = computed(() =>
  (summary.value?.by_assistant ?? []).slice(0, 8),
);

async function reload(): Promise<void> {
  loading.value = true;
  try {
    const [list, sum] = await Promise.all([
      listAccessRequests(which.value, props.devUser),
      getAccessRequestSummary(props.devUser),
    ]);
    rows.value = list;
    summary.value = sum;
  } catch (e) {
    toast.error(
      "Could not load access requests",
      e instanceof AccessRequestError ? e.message : String(e),
    );
  } finally {
    loading.value = false;
  }
}

function setWhich(w: "open" | "all"): void {
  if (which.value === w) return;
  which.value = w;
  void reload();
}

async function decide(
  r: AdminAccessRequestRow,
  status: "granted" | "rejected" | "handled",
): Promise<void> {
  busyId.value = r.id;
  try {
    await handleAccessRequest(
      r.id,
      status,
      notes.value[r.id] ?? "",
      props.devUser,
    );
    toast.success(`Marked ${status}`);
    window.dispatchEvent(new Event("choir:admin-counts-stale"));
    await reload();
  } catch (e) {
    toast.error(
      "Could not update the request",
      e instanceof AccessRequestError ? e.message : String(e),
    );
  } finally {
    busyId.value = null;
  }
}

function when(ts: string | null): string {
  if (!ts) return "—";
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

function accessLabel(v: boolean | null): string {
  if (v === true) return "Has access";
  if (v === false) return "No access";
  return "Unknown";
}

onMounted(reload);
</script>

<template>
  <section class="areq">
    <div v-if="summary" class="areq__summary">
      <div class="areq__totals">
        <span
          ><strong>{{ summary.open_now }}</strong> open now</span
        >
        <span class="areq__sub">
          Last {{ summary.days }} days: {{ summary.totals.total }} request(s) ·
          {{ summary.totals.granted }} granted ·
          {{ summary.totals.rejected }} rejected ·
          {{ summary.totals.handled }} handled
        </span>
      </div>
      <table v-if="topAssistants.length" class="areq__table areq__table--sum">
        <thead>
          <tr>
            <th>Assistant</th>
            <th>Requests</th>
            <th>Open</th>
            <th>Granted</th>
            <th>Rejected</th>
            <th>Handled</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="e in topAssistants" :key="e.assistant_slug">
            <td>{{ e.assistant_name }}</td>
            <td>{{ e.total }}</td>
            <td>{{ e.open }}</td>
            <td>{{ e.granted }}</td>
            <td>{{ e.rejected }}</td>
            <td>{{ e.handled }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <header class="areq__head">
      <div class="areq__seg" role="group" aria-label="Show">
        <button
          :class="['areq__segbtn', which === 'open' && 'areq__segbtn--on']"
          :aria-pressed="which === 'open'"
          @click="setWhich('open')"
        >
          Open
        </button>
        <button
          :class="['areq__segbtn', which === 'all' && 'areq__segbtn--on']"
          :aria-pressed="which === 'all'"
          @click="setWhich('all')"
        >
          All
        </button>
      </div>
      <button class="areq__btn" :disabled="loading" @click="reload">
        {{ loading ? "Loading…" : "Refresh" }}
      </button>
    </header>
    <p class="areq__help">
      Give access the usual way — add the person to a team or as a named member
      under Assistants — then mark the request here. Marking it does not change
      anyone's access.
    </p>

    <SkeletonRows v-if="loading && !rows.length" :rows="5" />

    <p v-else-if="!rows.length" class="areq__empty">
      {{
        which === "open"
          ? "No open requests."
          : "Nobody has requested access to an assistant yet."
      }}
    </p>

    <div v-else class="areq__scroll">
      <table class="areq__table">
        <thead>
          <tr>
            <th>Person</th>
            <th>Assistant</th>
            <th>Reason</th>
            <th>Requested</th>
            <th>Status</th>
            <th title="Re-checked now with the same rules the chat uses">
              Access now
            </th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="r in rows" :key="r.id">
            <td class="areq__who">
              <span>{{ r.user_name || r.user_email || r.user_oid }}</span>
              <span v-if="r.user_name && r.user_email" class="areq__sub">
                {{ r.user_email }}
              </span>
            </td>
            <td>{{ r.assistant_name }}</td>
            <td class="areq__reason">{{ r.reason || "—" }}</td>
            <td class="areq__when">{{ when(r.created_at) }}</td>
            <td>
              <span :class="['areq__status', `areq__status--${r.status}`]">
                {{ r.status }}
              </span>
              <span
                v-if="r.status !== 'open' && r.handler_note"
                class="areq__sub areq__note"
              >
                {{ r.handler_note }}
              </span>
            </td>
            <td>
              <span
                :class="[
                  'areq__access',
                  r.has_access === true && 'areq__access--yes',
                ]"
                :title="
                  r.has_access === null
                    ? 'Could not work out this person\'s groups'
                    : 'Best effort: groups as the directory reports them now'
                "
              >
                {{ accessLabel(r.has_access) }}
              </span>
            </td>
            <td class="areq__actions">
              <template v-if="r.status === 'open'">
                <input
                  v-model="notes[r.id]"
                  class="areq__noteinput"
                  type="text"
                  maxlength="1000"
                  placeholder="Note (optional)"
                  :aria-label="`Note for request ${r.id}`"
                />
                <div class="areq__btns">
                  <button
                    class="areq__btn"
                    :disabled="busyId === r.id"
                    @click="decide(r, 'granted')"
                  >
                    Mark granted
                  </button>
                  <button
                    class="areq__btn"
                    :disabled="busyId === r.id"
                    @click="decide(r, 'rejected')"
                  >
                    Mark rejected
                  </button>
                  <button
                    class="areq__btn"
                    :disabled="busyId === r.id"
                    @click="decide(r, 'handled')"
                  >
                    Mark handled
                  </button>
                </div>
              </template>
              <span v-else class="areq__sub">{{ when(r.handled_at) }}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>

<style scoped>
.areq__summary {
  margin-bottom: var(--space-4);
  padding: var(--space-3);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
}
.areq__totals {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-3);
  align-items: baseline;
  margin-bottom: var(--space-2);
}
.areq__sub {
  display: block;
  color: var(--color-muted);
  font-size: 0.82rem;
}
.areq__totals .areq__sub {
  display: inline;
}
.areq__head {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  align-items: center;
  justify-content: space-between;
}
.areq__help {
  margin: var(--space-2) 0 var(--space-3);
  max-width: 48rem;
  color: var(--color-muted);
  font-size: 0.86rem;
}
.areq__seg {
  display: inline-flex;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-pill, 999px);
  overflow: hidden;
}
.areq__segbtn {
  padding: 0.35rem 0.9rem;
  border: none;
  background: transparent;
  color: var(--color-muted);
  font: inherit;
  font-size: 0.85rem;
  cursor: pointer;
}
.areq__segbtn--on {
  background: var(--color-accent);
  color: var(--color-inverse, #fff);
}
.areq__btn {
  padding: 0.3rem 0.7rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-pill, 999px);
  background: var(--color-surface);
  color: inherit;
  font-size: 0.8rem;
  cursor: pointer;
  white-space: nowrap;
}
.areq__btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.areq__empty {
  color: var(--color-muted);
}
.areq__scroll {
  overflow-x: auto;
}
.areq__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.88rem;
}
.areq__table th,
.areq__table td {
  text-align: left;
  padding: 0.45rem 0.55rem;
  border-bottom: 1px solid var(--color-border);
  vertical-align: top;
}
.areq__table th {
  font-size: 0.76rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--color-muted);
}
.areq__table--sum {
  max-width: 40rem;
}
.areq__reason {
  min-width: 14rem;
  overflow-wrap: anywhere;
}
.areq__when {
  white-space: nowrap;
  color: var(--color-muted);
}
.areq__status {
  padding: 0.05rem 0.45rem;
  border-radius: var(--radius-pill, 999px);
  border: 1px solid var(--color-border);
  font-size: 0.75rem;
  text-transform: capitalize;
}
.areq__status--open {
  border-color: var(--color-accent);
  color: var(--color-accent);
}
.areq__note {
  margin-top: 0.2rem;
  max-width: 14rem;
}
.areq__access {
  font-size: 0.8rem;
  color: var(--color-muted);
  white-space: nowrap;
}
.areq__access--yes {
  color: var(--color-success, var(--color-accent));
  font-weight: 600;
}
.areq__actions {
  min-width: 16rem;
}
.areq__noteinput {
  width: 100%;
  margin-bottom: 0.35rem;
  padding: 0.3rem 0.5rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  color: inherit;
  font: inherit;
  font-size: 0.82rem;
}
.areq__btns {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
}
</style>
